/** Carrier to'lovlari: muddati o'tgan (qizil) va N kun ichida kelganlar, qolgan summa (USD). Bosilsa to'lovlar sahifasi. */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CircleDollarSign } from "lucide-react";
import { cn, formatDate, formatMoney } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useWidgetData } from "../data";
import { BigNumber, WidgetEmpty, WidgetRow } from "../ui";
import type { FeesConfig } from "./index";

type Props = { item: WidgetItem<FeesConfig>; me: Me };
interface FeeRow {
  application_id: number;
  driver_id: number;
  name: string;
  carrier: string;
  due: string;
  remaining: string;
  partial: boolean;
}
interface Data {
  overdue: FeeRow[];
  soon: FeeRow[];
  overdue_count: number;
  soon_count: number;
  overdue_total: string;
  soon_total: string;
  days: number;
  today: string;
}
const ENTITIES = ["application", "transaction"];
const PLACEMENTS = "/budget/placements";

function Row({ r, overdue }: { r: FeeRow; overdue: boolean }) {
  const navigate = useNavigate();
  return (
    <WidgetRow
      title={r.name}
      sub={r.carrier}
      dot={overdue ? "bg-danger" : "bg-warning"}
      right={
        <span className={cn(overdue && "font-semibold text-danger")}>
          {formatMoney(Number(r.remaining), "USD")} · {formatDate(r.due)}
        </span>
      }
      onClick={() => navigate(PLACEMENTS)}
    />
  );
}

export function FeesView({ item }: Props) {
  const { t } = useTranslation("widgets");
  const navigate = useNavigate();
  const q = useWidgetData<Data>("fees", { days: item.config.days }, ENTITIES);
  const d = q.data;
  if (!d) return q.isError ? <WidgetEmpty text={t("failed")} /> : null;
  const overdue = Number(d.overdue_total);
  const soon = Number(d.soon_total);
  const none = d.overdue_count === 0 && d.soon_count === 0;
  const numbers = (
    <div className={cn("flex gap-4", item.size === "s" && "flex-col gap-1.5")}>
      <BigNumber value={formatMoney(overdue, "USD")} label={t("fees.overdue", { count: d.overdue_count })} tone={d.overdue_count ? "text-danger" : "text-muted"} />
      {item.size !== "s" && <BigNumber value={formatMoney(soon, "USD")} label={t("fees.soon", { count: d.soon_count, days: d.days })} tone={d.soon_count ? undefined : "text-muted"} />}
    </div>
  );
  if (item.size === "s") {
    return (
      <button type="button" onClick={() => navigate(PLACEMENTS)} className="flex h-full w-full flex-col px-3 py-2.5 text-left">
        {numbers}
        <div className="mt-auto truncate text-[11px] text-muted tabular-nums">{t("fees.soonShort", { amount: formatMoney(soon, "USD"), count: d.soon_count, days: d.days })}</div>
      </button>
    );
  }
  const rows = [...d.overdue.map((r) => ({ r, overdue: true })), ...d.soon.map((r) => ({ r, overdue: false }))].slice(0, item.size === "m" ? 2 : 10);
  return (
    <div className="flex h-full flex-col px-3 py-2.5">
      {numbers}
      <div className={cn("mt-2 min-h-0 flex-1 border-t border-line pt-1.5", item.size === "l" && "overflow-y-auto")}>
        {rows.length ? rows.map(({ r, overdue: o }) => <Row key={r.application_id} r={r} overdue={o} />) : none && <WidgetEmpty text={t("fees.empty")} />}
      </div>
    </div>
  );
}

export function FeesGlance({ item }: Props) {
  const { t } = useTranslation("widgets");
  const q = useWidgetData<Data>("fees", { days: item.config.days }, ENTITIES);
  const n = q.data?.overdue_count ?? 0;
  return (
    <span className="inline-flex items-center gap-1.5" title={t("fees.title")}>
      <CircleDollarSign size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className={cn("text-[12px] font-semibold tabular-nums", n > 0 ? "text-danger" : "text-muted")}>{n}</span>
    </span>
  );
}
